/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const debounce = (fn, wait) => {
	let t;
	return (...args) => {
		clearTimeout(t);
		t = setTimeout(() => fn.apply(this, args), wait);
	};
};

window.addEventListener('DOMContentLoaded', (event) => {
	document.addEventListener('click', (event) => {
		const source = event.target;

		if (source.classList.contains('fetch-section-button')) {
			event.preventDefault();
			source.classList.add('is-loading');
			const sectionUrl = source.getAttribute('data-render-section-url');
			const sectionName = source.getAttribute('data-render-section-name');
			const sectionVariantId = source.getAttribute('data-variant-id');

			if (!sectionUrl || !sectionName || !sectionVariantId) {
				return;
			}

			if (source.parentElement.querySelector('[data-external-button-remove]')) {
				source.parentElement.querySelector('[data-external-button-remove]').remove();
			}

			if (document.querySelector(`${sectionName}[data-variant-id="${sectionVariantId}"]`)) {
				document
					.querySelector(`${sectionName}[data-variant-id="${sectionVariantId}"]`)
					.setAttribute('open', '');
				source.classList.remove('is-loading');
			} else {
				fetch(sectionUrl, {
					headers: {
						'X-Requested-With': 'XMLHttpRequest',
					},
				})
					.then((response) => response.text())
					.then((html) => {
						const content = new DOMParser().parseFromString(html, 'text/html');
						const section = content.querySelector(sectionName);

						if (section) {
							if (
								document.querySelectorAll('quick-buy-drawer[open]').length > 0 &&
								section.querySelector('.section-inner-content')
							) {
								const innerSection = section.querySelector('.section-inner-content');
								innerSection.setAttribute('data-external-button-remove', '');

								source.insertAdjacentElement('afterend', innerSection);
							} else {
								document.body.appendChild(section);
							}
						}
					})
					.catch((error) => {
						console.error('Error:', error);
					})
					.finally(() => {
						source.classList.remove('is-loading');
					});
			}
		}
	});
});

class ProductTabsContent extends HTMLElement {
	constructor() {
		super();
		document.addEventListener('variant:changed', (event) => {
			let variant = event.detail.variant; // Gives you access to the whole variant details
			let form = event.target;

			if (variant) {
				this.changeElementsOnVariantChange(variant);
			}
		});

		document.addEventListener('init:variant', (event) => {
			let variant = event.detail.variant; // Gives you access to the whole variant details
			let form = event.target;

			if (variant) {
				this.changeElementsOnVariantChange(variant);
			}
		});
	}

	changeElementsOnVariantChange(variant) {
		const elementsToCheck = this.querySelectorAll('[data-on-variant-change]');

		elementsToCheck.forEach((element) => {
			const variantsToCheck = element
				.getAttribute('data-variant-options')
				.split(',')
				.map((variant) => variant.trim())
				.map((variant) => variant.toLowerCase());
			const currentVariant = variant['options'].map((variant) => variant.toLowerCase());

			if (variantsToCheck.some((variant) => currentVariant.includes(variant))) {
				element.classList.remove('is-hidden');
			} else {
				element.classList.add('is-hidden');

				if (element.querySelector('collapsible-content')) {
					triggerEvent(element.querySelector('collapsible-content'), 'openable-element:close');
				}
			}
		});
	}
}

customElements.define('product-tabs-content', ProductTabsContent);

if (typeof SearchForm !== 'function') {
	class SearchForm extends HTMLElement {
		constructor() {
			super();

			this.initalPossition = window.innerWidth < 1024 ? 'mobile' : 'desktop';
			this.cachedResults = {};
			this.input = this.querySelector('[data-js-search-input]');
			this.predictiveSearchResults = this.querySelector('[data-js-search-results]');
			this.results = [];
			this.preloader = false;
			this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
			this.mainHeader = document.querySelector('store-header.header');
			this.mobileParent = document.querySelector('.header__wrapper-search');
			this.desktopParent = document.querySelector('.header__inline-navigation');
			this.searchFormHeaderClose = document.querySelector('.header__search-bar .predictive-search__search-close');
			this.searchFormHeaderSearch = document.querySelector('.header__search-bar .predictive-search__search-icon');
			this.searchBoxTrigger = document.querySelector('.header__icon-wrapper[aria-controls="search-box"]');

			let inputValue = this.input.value;

			this.checkPosition();

			window.addEventListener(
				'resize',
				debounce(() => {
					this.initalPossition = window.innerWidth < 1024 ? 'mobile' : 'desktop';
					this.checkPosition();
				}, 50)
			);

			this.input.addEventListener(
				'keyup',
				debounce((e) => {
					if (this.input.value != inputValue) {
						inputValue = this.input.value;
						this.addOverlay();
						this.getSearchResults(this.input.value.trim());
					} else if (this.input.value == '') {
						this.clearSearchResults();
					}
				}, 150)
			);

			this.input.addEventListener('keydown', (e) => {
				if (this.a11yIndex != -1) {
					if (
						e.keyCode === window.KEYCODES.UP ||
						e.keyCode === window.KEYCODES.DOWN ||
						e.keyCode === window.KEYCODES.RETURN
					) {
						e.preventDefault();
					}
				}
			});

			this.input.addEventListener('keyup', (e) => {
				if (e.keyCode === window.KEYCODES.UP) {
					this.navigateThrough('UP');
					return true;
				}

				if (e.keyCode === window.KEYCODES.DOWN) {
					this.navigateThrough('DOWN');
					return true;
				}

				if (e.keyCode === window.KEYCODES.RETURN) {
					if (this.a11yIndex != -1) {
						document.location.href =
							this.results[this.a11yIndex].tagName == 'A'
								? this.results[this.a11yIndex].href
								: this.results[this.a11yIndex].querySelector('a').href;
					}
				}
			});

			this.searchOverlay.addEventListener('click', (e) => {
				if (e.target == this.searchOverlay) {
					this.clearSearchResults();
				}
			});

			this.searchFormHeaderClose
				? this.searchFormHeaderClose.addEventListener('click', (e) => {
						this.clearSearchResults();
				  })
				: '';
		}

		checkPosition() {
			if (this.initalPossition == 'desktop') {
				if (this.mobileParent && this.mobileParent.querySelector('search-form')) {
					this.desktopParent.appendChild(this);
					this.classList.remove('header__search-bar--mobile-open');
					this.classList.remove('header__search-bar--mobile-closed');
				}
			} else {
				if (this.desktopParent && this.desktopParent.querySelector('search-form')) {
					this.mobileParent.appendChild(this);
					this.classList.remove('header__search-bar--mobile-open');
					this.classList.add('header__search-bar--mobile-closed');
				}

				if (this.searchBoxTrigger) {
					this.searchBoxTrigger.onclick = (e) => {
						e.preventDefault();
						this.toggleSearchBar();
					};
				}
			}
		}

		toggleSearchBar() {
			this.classList.toggle('header__search-bar--mobile-open');
			this.classList.toggle('header__search-bar--mobile-closed');
		}

		getSearchResults(query = '') {
			this.preloadSearchResults();

			const queryKey = query.replace(' ', '-').toLowerCase();

			if (this.cachedResults[queryKey]) {
				this.renderSearchResults(this.cachedResults[queryKey]);
				return;
			}

			this.a11yIndex = -1;

			if (query != '') {
				fetch(
					`${window.themeVariables.routes.predictiveSearchUrl}?q=${encodeURIComponent(
						query
					)}${window.themeVariables.routes.predictiveSearchResults}`
				)
					.then((response) => {
						if (!response.ok) {
							var error = new Error(response.status);
							this.clearSearchResults();
							throw error;
						}
						return response.text();
					})
					.then((text) => {
						const results = new DOMParser()
							.parseFromString(text, 'text/html')
							.querySelector('#shopify-section-predictive-search').innerHTML;
						this.cachedResults[queryKey] = results;
						this.renderSearchResults(results);
					})
					.catch((e) => {
						throw e;
					});
			} else {
				this.clearSearchResults();
			}
		}

		clearSearchResults() {
			this.predictiveSearchResults.innerHTML = '';
			this.preloader = false;
			this.results = [];
			this.searchFormHeaderClose ? this.searchFormHeaderClose.setAttribute('hidden', '') : '';
			this.searchFormHeaderSearch ? this.searchFormHeaderSearch.removeAttribute('hidden') : '';

			if (this.searchOverlay) {
				this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
				this.mainHeader.classList.remove('header-search--opened');
				this.input.value = '';
			}
		}

		addOverlay() {
			if (this.searchOverlay) {
				this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
				this.mainHeader.classList.add('header-search--opened');
			}
		}

		preloadSearchResults() {
			if (!this.preloader) {
				this.preloader = true;
				this.predictiveSearchResults.innerHTML = `<p class="text--center text--large">${window.themeVariables.strings.searchLoading}</p>`;
			}
		}

		renderSearchResults(results) {
			this.predictiveSearchResults.innerHTML = results;
			this.preloader = false;
			this.results = this.predictiveSearchResults.querySelectorAll('[data-js-search-item]');
			this.searchFormHeaderClose ? this.searchFormHeaderClose.removeAttribute('hidden') : '';
			this.searchFormHeaderSearch ? this.searchFormHeaderSearch.setAttribute('hidden', '') : '';
		}

		navigateThrough(direction) {
			if (this.a11yIndex == -1) {
				this.a11yIndex = 0;
			} else {
				this.results[this.a11yIndex].classList.remove('active');
				if (direction == 'UP') {
					if (this.a11yIndex - 1 >= -1) {
						this.a11yIndex--;
					}
				} else if (direction == 'DOWN') {
					if (this.a11yIndex + 1 < this.results.length) {
						this.a11yIndex++;
					}
				}
			}

			if (this.a11yIndex >= 0) {
				this.results[this.a11yIndex].classList.add('active');
				document.querySelector('[data-js-search-results-holder]').scrollTop =
					this.results[this.a11yIndex].offsetTop - 200;
			} else {
				document.querySelector('[data-js-search-results-holder]').scrollTop = 0;
			}
		}
	}

	if (typeof customElements.get('search-form') == 'undefined') {
		customElements.define('search-form', SearchForm);
	}
}


if (typeof ScriptExecuter !== 'function') {
	class ScriptExecuter extends HTMLElement {
		constructor() {
			super();
			
			this.codeToExecute = this.textContent;
		}

		connectedCallback() {
			this.executeScript();
		}

		executeScript() {
			const script = document.createElement("script");
			script.textContent = this.codeToExecute;
			script.type = "text/javascript";
			script.onload = () => {
				this.remove();
			};

			document.body.appendChild(script);
		}
	}

	if (typeof customElements.get('script-executer') == 'undefined') {
		customElements.define('script-executer', ScriptExecuter);
	}
}

// if (typeof FetchProductDetails !== 'function') {
// 	class FetchProductDetails extends HTMLElement {
// 		constructor() {
// 			super();

// 			this.url = this.getAttribute('data-url');
// 			this.data = false;

// 			if (this.url) {
// 				this.fetchProductDetails();
// 			}
// 		}

// 		fetchProductDetails () {
// 			fetch(this.url).then((response) => response.json()).then((data) => this.data = data);
// 		}
// 	}

// 	if (typeof customElements.get('product-details-form-fetch') == 'undefined') {
// 		customElements.define('product-details-form-fetch', FetchProductDetails);
// 	}
// }